import type { AdditionalFilterValues } from "@bbj-types/candidate";
import type { ICandidateFieldSchemaWithOptions } from "@bbj-types/organisation";
import { TextField } from "@mui/material";
import { isAdditionalDateFilterValue } from "@root/utils/typeguards";
import { AutocompleteCheckboxes, LabeledSwitch } from "@styleguide";
import {
  dateRangePickerChipDoneToDate,
  dateRangePickerChipLastSixtyDays,
  dateRangePickerChipLastThirtyDays,
  dateRangePickerChipMonth,
  dateRangePickerChipNextSixtyDays,
  dateRangePickerChipNextThirtyDays,
  dateRangePickerChipNextWeek,
  dateRangePickerChipWeek,
} from "../../inputs/date/DateRangePicker/chips";
import { DateRangePickerTextField } from "../../inputs/date/DateRangePicker/textfield";
import type { IDateRangePickerChip } from "../../inputs/date/DateRangePicker/types";

export const additionalFieldFilterChips = [
  dateRangePickerChipDoneToDate,
  dateRangePickerChipLastSixtyDays,
  dateRangePickerChipLastThirtyDays,
  dateRangePickerChipWeek,
  dateRangePickerChipMonth,
  dateRangePickerChipNextWeek,
  dateRangePickerChipNextThirtyDays,
  dateRangePickerChipNextSixtyDays,
] as const satisfies IDateRangePickerChip[];

export type TAdditionalFieldFilterChips = (typeof additionalFieldFilterChips)[number]["value"];

export interface IAdditionalField {
  fieldSchema: Pick<ICandidateFieldSchemaWithOptions, "label" | "type" | "isActive" | "options">;
  onChange: (value: AdditionalFilterValues | null) => void;
  value: AdditionalFilterValues | null;
}

export const AdditionalField = ({ fieldSchema, onChange, value }: IAdditionalField) => {
  switch (fieldSchema.type) {
    case "number":
    case "text":
      return (
        <TextField
          label={fieldSchema.label}
          variant="outlined"
          size="small"
          type={fieldSchema.type}
          fullWidth
          value={["string", "number"].includes(typeof value) ? value : ""}
          onChange={(e) => onChange(fieldSchema.type === "number" ? +e.target.value : e.target.value)}
          disabled={!fieldSchema.isActive}
        />
      );
    case "list": {
      const listOptions =
        fieldSchema.options?.values.map((opt) => ({
          label: opt.label,
          value: opt.value,
          group: opt.group ?? "",
        })) ?? [];
      const selectedOptions = Array.isArray(value) ? listOptions.filter((opt) => value.includes(opt.value)) : [];
      return (
        <AutocompleteCheckboxes
          options={listOptions}
          value={selectedOptions}
          onChange={(_, values) => onChange(values.map((v) => v.value))}
          disabled={!fieldSchema.isActive}
          size="small"
          TextFieldProps={{ label: fieldSchema.label }}
          sx={{ minWidth: 0 }}
          slotProps={{
            paper: {
              sx: {
                width: "100%",
              },
            },
          }}
          fullWidth
        />
      );
    }
    case "bool":
      return (
        <LabeledSwitch
          label={fieldSchema.label}
          value={typeof value === "boolean" ? value : false}
          onChange={(_, checked) => onChange(checked)}
        />
      );
    case "date": {
      const dateValue = isAdditionalDateFilterValue(value) ? value : { range: { startDate: null, endDate: null } };
      return (
        <DateRangePickerTextField
          label={fieldSchema.label}
          value={dateValue.range}
          onChange={(range, chip) => onChange({ range, chip })}
          disabled={!fieldSchema.isActive}
          chips={additionalFieldFilterChips}
          chip={dateValue.chip}
          fullWidth
        />
      );
    }
  }
};
